import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {
    Alert,
    FlatList,
    Keyboard,
    Linking,
    Modal,
    Platform,
    ScrollView,
    StyleSheet,
    Switch,
    Text,
    TextInput,
    TouchableOpacity,
    useWindowDimensions,
    View,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import ErrorReportingService from "../helpers/ErrorReportingService";
import {LinearGradient} from "expo-linear-gradient";
import Feather from 'react-native-vector-icons/Feather';
import Spinner from "react-native-loading-spinner-overlay";
import {priceFormatter} from "../helpers/numbers";
import VivaPaymentModule from "../paymentModules/VivaPaymentsModule";
import {CartContext} from "../helpers/CartContext";
import {BluetoothContext} from "../helpers/BluetoothContext";
import {OrdersContext} from "../helpers/OrdersContext";
import {handlePayment, send_storeOrder} from "../helpers/paymentHelper";
import VatModal from "../components/VatModal";
import {TableContext} from "../helpers/TableContext";
import {hungarianRoundNumbers} from '../helpers/hungarianRoundNumbers';
import PinPad from "../components/PinPad";
import {VipCardContext} from "../helpers/VipCardContext";
import {useTranslation} from "react-i18next";
import ItemComponent from "../components/ItemComponent";
import Observables from "@happy-gastro/global-observables";
import {hasAnyConfiguredPrinter, RawBTPrintKitchen, RawBTPrintTicket} from "../helpers/rawBTDriver";
import HappyAlert from "../helpers/crossPlatformTools/HappyAlert";
import TeyaPaymentDialog from "../components/TeyaPaymentDialog";
import NpipePaymentDialog from "../components/NpipePaymentDialog";
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import { ModalKeyboardAvoider } from '../components/KeyboardAware';
import {useTheme} from '../helpers/ThemeContext';
import {useThemedStyles} from '../helpers/useThemedStyles';
import {POS_TABLET_BREAKPOINT, usePosLayout} from '../helpers/posLayout';
import {haptics} from '../helpers/haptics';

// Cross-platform toast: real ToastAndroid on Android, toast-message shim on iOS/web.
const ToastAndroid = require('../helpers/crossToast').default;

const VoucherModal = ({
    visible,
    onClose,
    onConfirm,
    voucherCode,
    onVoucherCodeChange,
    styles
}) => {
    const { t } = useTranslation();
    return (
        <Modal
            visible={visible}
            transparent={true}
            animationType="fade"
            onRequestClose={onClose}
        >
            <ModalKeyboardAvoider style={styles.modalContainer} justify="center">
                <View style={styles.modalContent}>
                    <Text style={styles.modalTitle}>{t('voucher')}</Text>

                    <TextInput
                        style={styles.input}
                        value={voucherCode}
                        onChangeText={onVoucherCodeChange}
                        placeholder={t('Enter_voucher_code')}
                        autoCapitalize="characters"
                    />

                    <View style={styles.modalButtons}>
                        <TouchableOpacity
                            style={styles.modalButton}
                            onPress={onClose}
                        >
                            <Text>{t('cancel')}</Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                            style={styles.modalButton}
                            onPress={onConfirm}
                        >
                            <Text>{t('confirm')}</Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </ModalKeyboardAvoider>
        </Modal>
    );
};

const PaymentScreen = ({ navigation, setIsModalVisible }) => {
    const insets = useSafeAreaInsets();
    const {width: screenWidth, height: screenHeight} = useWindowDimensions();
    const {isPhoneUi} = usePosLayout();
    const { theme, isDark } = useTheme();
    const styles = useThemedStyles(buildStyles);
    const { cartTotal = 0, setCart, cartItems = [], originalCartTotal, productsPrice, discountsForWholeCart, setCartItems, serviceFeeAmount, cartTotalWithServiceFee, alreadySentCartItems, serviceFeePercentage, setAlreadySentCartItems } = useContext(CartContext);
    const { currentTable } = useContext(TableContext);
    const { selectedVipCard, removeVipCard, selectedEmployee, removeEmployee } = useContext(VipCardContext);
    const { sendData } = useContext(BluetoothContext);
    const [processing, setProcessing] = useState(false);
    const [needInvoice, setNeedInvoice] = useState(false);
    const [needNAV, setNeedNAV] = useState(true);
    // Mirror for the Viva deep-link callback, which is registered once and would
    // otherwise read the first render's value of the switch.
    const needNAVRef = useRef(needNAV);
    needNAVRef.current = needNAV;
    const [paymentType, setPaymentType] = useState(false);
    const [originalAmount, setOriginalAmount] = useState(cartTotal || 0);
    const [amount, setAmount] = useState(cartTotal || 0);
    const [paidAmount, setPaidAmount] = useState(0);
    const [amountToPay, setAmountToPay] = useState((originalAmount));
    const [changeToReturn, setChangeToReturn] = useState(0);
    const [modalVisible, setModalVisible] = useState(false);
    const [vatInvoiceData, setVatInvoiceData] = useState(false);
    const [isSplitOrder, setIsSplitOrder] = useState(true);
    const [payments, setPayments] = useState([]);
    const [paymentsSum, setPaymentsSum] = useState(0);
    const { orders, upsertOrder } = useContext(OrdersContext);
    const [showSplitItems, setShowSplitItems] = useState(false);
    const [paymentsModal, setPaymentsModal] = useState(false);
    const [selectedItemsToPay, setSelectedItemsToPay] = useState([]);
    const { t } = useTranslation();
    const [combinedCartItems, setCombinedCartItems] = useState([...alreadySentCartItems.map(item => ({ ...item, canBeModified: false })), ...cartItems.map(item => ({ ...item, canBeModified: true })).reverse()] || []);
    const [selectedItemsSum, setSelectedItemsSum] = useState(0);
    const textInputRef = useRef(null);
    const paymentInFlightRef = useRef(false);
    const [voucherModalVisible, setVoucherModalVisible] = useState(false);
    const [isAlertVisible, setIsAlertVisible] = useState(false);
    const [voucherCode, setVoucherCode] = useState('');
    const [showTipButton, setShowTipButton] = useState(true);
    const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
    const [keyboardHeight, setKeyboardHeight] = useState(0);
    const isCompactDevice = screenWidth < POS_TABLET_BREAKPOINT;
    const isShortViewport = screenHeight < 920;
    const isNarrowDesktop = !isCompactDevice && screenWidth < 1380;
    const useDenseLayout = isCompactDevice || isShortViewport || isNarrowDesktop;
    const useStickyFooter = true;
    const stickyFooterBaseHeight = isCompactDevice
        ? (showTipButton ? (useDenseLayout ? 150 : 166) : (useDenseLayout ? 110 : 116))
        : (showTipButton ? (useDenseLayout ? 92 : 100) : (useDenseLayout ? 84 : 90));
    const bottomActionInset = Math.max(insets.bottom + (isCompactDevice ? 8 : 0), Platform.OS === 'android' ? 22 : 10);
    const compactFooterSpacer = stickyFooterBaseHeight + bottomActionInset + (useDenseLayout ? 12 : 18);
    const pinPadHeight = isCompactDevice
        // Phone UI: a taller pad (≈ 60–80dp keys) — the old 30% made 45–55dp keys.
        ? (isKeyboardVisible ? 0 : (isPhoneUi ? Math.max(240, Math.min(screenHeight * 0.36, 320)) : Math.max(180, Math.min(screenHeight * 0.30, 280))))
        : useDenseLayout
            ? Math.max(180, Math.min(screenHeight * 0.25, 250))
        : Math.max(220, Math.min(screenHeight * 0.31, 320));

    // Memoize combinedCartItems to avoid unnecessary recalculations
    const combinedCartItemsMemo = useMemo(() => {
        return [...alreadySentCartItems.map(item => ({ ...item, canBeModified: false })),
        ...cartItems.map(item => ({ ...item, canBeModified: true })).reverse()];
    }, [alreadySentCartItems, cartItems]);

    useEffect(() => {
        setCombinedCartItems(combinedCartItemsMemo);
    }, [combinedCartItemsMemo]);

    // Memoize selectedItemsSum calculation
    const calculatedSelectedItemsSum = useMemo(() => {
        return selectedItemsToPay?.length > 0
            ? Math.round(selectedItemsToPay.reduce((sum, item) => sum + (item.remotePrice || item.price), 0))
            : 0;
    }, [selectedItemsToPay]);

    useEffect(() => {
        setSelectedItemsSum(calculatedSelectedItemsSum);
    }, [calculatedSelectedItemsSum]);

    useEffect(() => {
        const showSub = Keyboard.addListener('keyboardDidShow', (event) => {
            setIsKeyboardVisible(true);
            setKeyboardHeight(event?.endCoordinates?.height || 0);
        });
        const hideSub = Keyboard.addListener('keyboardDidHide', () => {
            setIsKeyboardVisible(false);
            setKeyboardHeight(0);
        });

        return () => {
            showSub.remove();
            hideSub.remove();
        };
    }, []);

    const shouldTreatSurplusAsChange = useMemo(() => {
        return !showTipButton;
    }, [showTipButton]);

    useEffect(() => {
        const loadPaymentUiSettings = async () => {
            try {
                // Default the "Cash register / fiscal printing" toggle to the actual
                // register selection: ON only when a cashier device is configured,
                // so fiscal printing can't be silently enabled with no register. (SVC-001)
                const selectedCashierDevice = await AsyncStorage.getItem('selectedCashierDevice');
                setNeedNAV(Boolean(selectedCashierDevice));

                const storedValue = await AsyncStorage.getItem('showTipButton');
                if (storedValue === null) {
                    setShowTipButton(true);
                    return;
                }

                setShowTipButton(storedValue === 'true');
            } catch (_error) {
                setShowTipButton(true);
            }
        };

        loadPaymentUiSettings();
        const unsubscribe = navigation.addListener('focus', loadPaymentUiSettings);
        return unsubscribe;
    }, [navigation]);

    const validateAndAddVoucher = () => {
        const voucherRegex = /^Kupon\d+$/;

        if (voucherRegex.test(voucherCode)) {
            const amount = parseInt(voucherCode.replace('Kupon', ''));

            setPayments([...payments, {
                paymentType: 'voucher',
                amount: amount
            }]);

            setVoucherModalVisible(false);
            setVoucherCode('');
        } else {
            setIsAlertVisible(true)
        }
    };

    useEffect(() => {
        if (currentTable) {
            const tableOrder = orders?.find(
                (order) =>
                    !order.storno &&
                    order.status !== "cancelled" &&
                    order.status !== "closed" &&
                    order.tableId === currentTable?._id
            );
            if (tableOrder?.items) {
                setAlreadySentCartItems(tableOrder?.items)
            }
        }

    }, [orders])


    const toggleModal = () => {
        setPaymentsModal(!paymentsModal);
    };
    // Memoize payments sum calculation
    const calculatedPaymentsSum = useMemo(() => {
        return payments.reduce((acc, payment) => acc + payment.amount, 0);
    }, [payments]);

    // Update paymentsSum and amount when payments change
    useEffect(() => {
        setPaymentsSum(calculatedPaymentsSum);
        setAmount(originalAmount - calculatedPaymentsSum);
    }, [calculatedPaymentsSum, originalAmount]);
    // Removed commented out debug logging

    // Memoize hungarianRoundNumbers to avoid repeated calculations with the same inputs
    const memoizedHungarianRound = useCallback((value) => {
        return hungarianRoundNumbers(value);
    }, []);

    // Memoize amount calculations based on payment type and selected items
    const amountCalculations = useMemo(() => {
        if (!currentTable) return { originalAmount: 0, amount: 0 };

        const isCashPayment = paymentType === 'Készpénz' || paymentType === 'cash';
        const hasSelectedItems = selectedItemsToPay?.length > 0;
        const serviceFee = Number(serviceFeePercentage);

        if (isCashPayment) {
            if (hasSelectedItems) {
                if (!isNaN(serviceFee)) {
                    const totalWithFee = (selectedItemsSum * serviceFee) + selectedItemsSum;
                    return {
                        originalAmount: memoizedHungarianRound(totalWithFee),
                        amount: memoizedHungarianRound(totalWithFee - paymentsSum)
                    };
                } else {
                    ErrorReportingService.error("Invalid serviceFeePercentage", { serviceFeePercentage }, "PaymentScreen");
                    return {
                        originalAmount: originalAmount, // Keep existing value
                        amount: memoizedHungarianRound(selectedItemsSum - paymentsSum)
                    };
                }
            } else {
                const roundedAmount = memoizedHungarianRound(cartTotalWithServiceFee);
                return {
                    originalAmount: roundedAmount,
                    amount: roundedAmount - paymentsSum
                };
            }
        } else {
            if (hasSelectedItems) {
                const totalWithFee = (selectedItemsSum * serviceFee) + selectedItemsSum;
                return {
                    originalAmount: Math.round(totalWithFee),
                    amount: Math.round(totalWithFee - paymentsSum)
                };
            } else {
                return {
                    originalAmount: cartTotalWithServiceFee,
                    amount: cartTotalWithServiceFee - paymentsSum
                };
            }
        }
    }, [currentTable, cartTotalWithServiceFee, selectedItemsToPay, selectedItemsSum, paymentType, serviceFeePercentage, paymentsSum, memoizedHungarianRound, originalAmount]);

    // Apply the calculated values
    useEffect(() => {
        if (currentTable) {
            setOriginalAmount(amountCalculations.originalAmount);
            setAmount(amountCalculations.amount);
        }
    }, [currentTable, amountCalculations]);

    const renderItem = ({ item }) => (
        <ItemComponent
            item={item}
            selectedItemsToPay={selectedItemsToPay}
            setSelectedItemsToPay={setSelectedItemsToPay}
            priceFormatter={priceFormatter}
            styles={styles}
        />
    );

    // Memoize translateDisplay function to prevent unnecessary re-renders
    const translateDisplay = useCallback((text) => {
        // Use the t function from the component scope instead of calling useTranslation() here
        if (text === "Készpénz") return t("cash");
        if (text === "Bankkártya") return t("credit_card");
        if (text === "Szép kártya") return t("Szép kártya");
        if (text === "Utalvány") return t("voucher");
        if (text === "VIP Számla") return t("vip_balance");
        if (text === "Személyzeti") return t("employee_balance");
        return text; // Return the original text if no translation is found
    }, [t]);
    // Memoize payment options to prevent unnecessary recalculations
    const paymentOptions = useMemo(() => {
        if (selectedVipCard) {
            return [
                { type: 'Készpénz', icon: 'dollar-sign' },
                { type: 'Bankkártya', icon: 'credit-card' },
                { type: 'Szép kártya', icon: 'credit-card' },
                { type: 'Utalvány', icon: 'tag' },
                { type: 'VIP Számla', icon: 'star' }, // Add VIP Balance payment type
            ];
        } else if (selectedEmployee) {
            return [
                { type: 'Készpénz', icon: 'dollar-sign' },
                { type: 'Bankkártya', icon: 'credit-card' },
                { type: 'Szép kártya', icon: 'credit-card' },
                { type: 'Utalvány', icon: 'tag' },
                { type: 'Személyzeti', icon: 'star' }, // Add employee Balance payment type
            ];
        } else {
            return [
                { type: 'Készpénz', icon: 'dollar-sign' },
                { type: 'Bankkártya', icon: 'credit-card' },
                { type: 'Szép kártya', icon: 'credit-card' },
                { type: 'Utalvány', icon: 'tag' },
            ];
        }
    }, [selectedVipCard, selectedEmployee]);

    // Memoize formatAmountWithSpaces function to prevent unnecessary re-renders
    const formatAmountWithSpaces = useCallback((value) => {
        if (!value) return '';
        if ((typeof value === 'number' && value < 0)) return '';

        // Round the value to 2 decimal places
        const roundedValue = Math.round(value * 100) / 100;

        // Split number into integer and decimal parts
        const [integerPart, decimalPart] = roundedValue.toString().split('.');

        // Add spaces for thousands in the integer part
        const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

        // Ensure the decimal part has 2 digits
        const formattedDecimal = decimalPart ? decimalPart.padEnd(2, '0') : '00';

        // Return formatted value without decimal part if both decimal digits are zero
        return formattedDecimal === '00' ? formattedInteger : `${formattedInteger}.${formattedDecimal}`;
    }, []);


    // Memoize reverseConvertPaymentType function to prevent unnecessary re-renders
    const reverseConvertPaymentType = useCallback((paymentTypeValue) => {
        if (paymentTypeValue === "cash") return "Készpénz";
        if (paymentTypeValue === "creditCard") return "Bankkártya";
        if (paymentTypeValue === "szepCreditCard") return "Szép kártya";
        if (paymentTypeValue === "voucher") return "Utalvány";
        if (paymentTypeValue === "vipBalance") return "VIP egyenleg";
        if (paymentTypeValue === "employeeBalance") return "Személyzeti";
        return `${paymentTypeValue}`;
    }, []);

    // Re-arm confirmPaymentAmount once handlePayment/the card terminal flow finished (processing reset)
    useEffect(() => {
        if (!processing) {
            paymentInFlightRef.current = false;
        }
    }, [processing]);

    const confirmPaymentAmount = async (isTip) => {
        // No double submit while a (card terminal) payment is already in flight
        if (paymentInFlightRef.current) {
            return;
        }
        paymentInFlightRef.current = true;
        try {
            setProcessing(true);
            const totalPaidAmount = isSplitOrder ? paymentsSum : paidAmount;
            const amountToBePaid = originalAmount;
            const surplus = totalPaidAmount - amountToBePaid;
            const tableOrder = orders?.find(
                (order) =>
                    !order.storno &&
                    order.status !== "cancelled" &&
                    order.status !== "closed" &&
                    order.tableId === currentTable?._id
            );
            if (totalPaidAmount > amountToBePaid) {
                const tip = (paymentType === 'Bankkártya' || paymentType === 'creditCard' || paymentType === 'szepCreditCard' || paymentType === 'Szép kártya') ? surplus : hungarianRoundNumbers(surplus);

                if (isTip && !shouldTreatSurplusAsChange) {
                    //Todo: test if works as expected
                    //Todo: test with creditcard if works as expected
                    if (selectedItemsToPay?.length > 0) {
                        //split
                        //Split payment with not exceeding payment
                        const filteredItemsArrayForSplitOrder = selectedItemsToPay ? alreadySentCartItems.map(item => {
                            const matchingItems = selectedItemsToPay.filter(selected => selected._id === item._id && selected.size === item.size);
                            if (matchingItems.length > 0) {
                                const totalAmountToDecrease = matchingItems.reduce((sum, selectedItem) => sum + 1, 0);
                                return {
                                    ...item,
                                    amount: Math.max((item.amount || 0) - totalAmountToDecrease, 0),
                                    quantity: Math.max((item.quantity || 0) - totalAmountToDecrease, 0)
                                };
                            }
                            return item;
                        }) : alreadySentCartItems;

                        //selectedItems payment
                        const splitCardResult = await handlePayment(paymentType, paidAmount, amountToBePaid, setProcessing, navigation, cartTotal, setCart, cartItems, sendData, () => {/*onClose*/
                        }, originalCartTotal, productsPrice, discountsForWholeCart, vatInvoiceData, needInvoice, false, currentTable, serviceFeeAmount, selectedItemsToPay, undefined, payments, tip, (selectedVipCard ? selectedVipCard : selectedEmployee), undefined, tableOrder, needNAV);
                        if (splitCardResult === false) {
                            // Card payment on the terminal did not complete: do not mark the items as paid
                            setProcessing(false);
                            return;
                        }

                        ErrorReportingService.info("Sending order to save", {
                            tableOrder: tableOrder?._id,
                            currentTable: currentTable?._id
                        }, "PaymentScreen");
                        //order update.
                        await send_storeOrder(cartTotal, cartItems, paymentType, paidAmount, discountsForWholeCart, vatInvoiceData, needInvoice, currentTable, serviceFeeAmount, true, filteredItemsArrayForSplitOrder, tableOrder._id, payments, tip, originalAmount, (selectedVipCard ? selectedVipCard : selectedEmployee), undefined, selectedItemsToPay, tableOrder);
                        Observables.call("reloadOrders");
                    } else {

                        return handlePayment(paymentType, paidAmount, amountToBePaid, setProcessing, navigation, cartTotal, setCart, cartItems, sendData, () => {/*onClose*/ }, originalCartTotal, productsPrice, discountsForWholeCart, vatInvoiceData, needInvoice, false, currentTable, serviceFeeAmount, alreadySentCartItems, tableOrder?._id, payments, tip, (selectedVipCard ? selectedVipCard : selectedEmployee), undefined, tableOrder, needNAV);
                    }
                } else {
                    //Todo: test if works as expected
                    //Todo: test with creditcard if works as expected
                    //this happens when the user clicks on the "Visszajáró" button
                    if (selectedItemsToPay?.length > 0) {
                        //split
                        const filteredItemsArrayForSplitOrder = selectedItemsToPay ? alreadySentCartItems.map(item => {
                            const matchingItems = selectedItemsToPay.filter(selected => selected._id === item._id && selected.size === item.size);
                            if (matchingItems.length > 0) {
                                const totalAmountToDecrease = matchingItems.length;
                                return {
                                    ...item,
                                    amount: Math.max((item.amount || 0) - totalAmountToDecrease, 0),
                                    quantity: Math.max((item.quantity || 0) - totalAmountToDecrease, 0)
                                };
                            }
                            return item;
                        }) : alreadySentCartItems;

                        //selectedItems payment
                        const splitCardResult = await handlePayment(paymentType, amountToBePaid, amountToBePaid, setProcessing, navigation, cartTotal, setCart, cartItems, sendData, () => {/*onClose*/
                        }, originalCartTotal, productsPrice, discountsForWholeCart, vatInvoiceData, needInvoice, false, currentTable, serviceFeeAmount, selectedItemsToPay, undefined, payments, 0, (selectedVipCard ? selectedVipCard : selectedEmployee), undefined, tableOrder, needNAV);
                        if (splitCardResult === false) {
                            // Card payment on the terminal did not complete: do not mark the items as paid
                            setProcessing(false);
                            return;
                        }

                        //order update.
                        await send_storeOrder(cartTotal, cartItems, paymentType, paidAmount, discountsForWholeCart, vatInvoiceData, needInvoice, currentTable, serviceFeeAmount, true, filteredItemsArrayForSplitOrder, tableOrder._id, payments, 0, originalAmount, (selectedVipCard ? selectedVipCard : selectedEmployee), undefined, selectedItemsToPay, tableOrder);
                        Observables.call("reloadOrders");
                    } else {
                        return handlePayment(paymentType, amountToBePaid, amountToBePaid, setProcessing, navigation, cartTotal, setCart, cartItems, sendData, () => {/*onClose*/
                        }, originalCartTotal, productsPrice, discountsForWholeCart, vatInvoiceData, needInvoice, false, currentTable, serviceFeeAmount, alreadySentCartItems, tableOrder?._id, payments, 0, (selectedVipCard ? selectedVipCard : selectedEmployee), undefined, tableOrder, needNAV);
                    }
                }
            } else {
                if (selectedItemsToPay?.length > 0) {
                    //Split payment with not exceeding payment
                    const filteredItemsArrayForSplitOrder = selectedItemsToPay ? alreadySentCartItems.map(item => {
                        const matchingItems = selectedItemsToPay.filter(selected => selected._id === item._id && selected.size === item.size);
                        if (matchingItems.length > 0) {
                            const totalAmountToDecrease = matchingItems.reduce((sum, selectedItem) => sum + 1, 0);
                            return {
                                ...item,
                                amount: Math.max((item.amount || 0) - totalAmountToDecrease, 0),
                                quantity: Math.max((item.quantity || 0) - totalAmountToDecrease, 0)
                            };
                        }
                        return item;
                    }) : alreadySentCartItems;
                    //selectedItems payment

                    const splitCardResult = await handlePayment(paymentType, paidAmount, amountToBePaid, setProcessing, navigation, cartTotal, setCart, cartItems, sendData, () => {/*onClose*/
                    }, originalCartTotal, productsPrice, discountsForWholeCart, vatInvoiceData, needInvoice, false, currentTable, serviceFeeAmount, selectedItemsToPay, undefined, payments, 0, (selectedVipCard ? selectedVipCard : selectedEmployee), undefined, tableOrder, needNAV);
                    if (splitCardResult === false) {
                        // Card payment on the terminal did not complete: do not mark the items as paid
                        setProcessing(false);
                        return;
                    }

                    //order update.
                    await send_storeOrder(cartTotal, cartItems, paymentType, paidAmount, discountsForWholeCart, vatInvoiceData, needInvoice, currentTable, serviceFeeAmount, true, filteredItemsArrayForSplitOrder, tableOrder._id, payments, 0, originalAmount, (selectedVipCard ? selectedVipCard : selectedEmployee), undefined, selectedItemsToPay, tableOrder);
                    Observables.call("reloadOrders");
                } else {
                    //not exceeding payment
                    ErrorReportingService.info("Processing non-exceeding payment", { paymentType }, "PaymentScreen");
                    return handlePayment(paymentType, paidAmount, amountToBePaid, setProcessing, navigation, cartTotal, setCart, cartItems, sendData, () => {/*onClose*/
                    }, originalCartTotal, productsPrice, discountsForWholeCart, vatInvoiceData, needInvoice, false, currentTable, serviceFeeAmount, alreadySentCartItems, tableOrder?._id, payments, 0, (selectedVipCard ? selectedVipCard : selectedEmployee), undefined, tableOrder, needNAV);
                }
            }
        } catch (error) {
            ErrorReportingService.error("Error in confirmPaymentAmount", { error }, "PaymentScreen");
            setProcessing(false); // Reset processing state on error
            if (Platform.OS === 'android') {
                ToastAndroid.showWithGravity(
                    t('payment_error_processing'),
                    ToastAndroid.LONG,
                    ToastAndroid.CENTER
                );
            } else {
                alert(t('payment_error_processing'));
            }
        } finally {
            // Note: We don't set processing to false here because handlePayment will do that
            // The catch block will handle errors and set processing to false if needed
        }
    };

    // Memoize payment calculations to avoid unnecessary recalculations
    const paymentCalculations = useMemo(() => {
        const numericAmount = parseInt(amount) || 0;
        const splitPaymentSum = paymentsSum;
        const roundedOriginalAmount = originalAmount;
        const remainingAmount = payments?.length > 0
            ? Math.max(roundedOriginalAmount - splitPaymentSum - numericAmount, 0)
            : Math.max(roundedOriginalAmount - numericAmount, 0);
        const change = payments?.length > 0
            ? Math.max(splitPaymentSum - roundedOriginalAmount, 0)
            : Math.max(numericAmount - roundedOriginalAmount, 0);

        // Calculate amountToPay based on payment type
        const calculatedAmountToPay = paymentType === 'Készpénz'
            ? memoizedHungarianRound(remainingAmount)
            : remainingAmount;

        return {
            paidAmount: numericAmount,
            amountToPay: calculatedAmountToPay,
            changeToReturn: change
        };
    }, [amount, originalAmount, payments, paymentType, paymentsSum, memoizedHungarianRound]);

    // Apply the calculated values
    useEffect(() => {
        setPaidAmount(paymentCalculations.paidAmount);
        setAmountToPay(paymentCalculations.amountToPay);
        setChangeToReturn(paymentCalculations.changeToReturn);
    }, [paymentCalculations]);


    // Memoize payment type specific calculations
    const paymentTypeCalculations = useMemo(() => {
        if (!currentTable) return { originalAmount: 0 };

        const isCashPayment = paymentType === 'Készpénz' || paymentType === 'cash';
        const hasSelectedItems = selectedItemsToPay?.length > 0;
        const serviceFee = Number(serviceFeePercentage);

        if (isCashPayment) {
            if (hasSelectedItems) {
                if (!isNaN(serviceFee)) {
                    const calculatedAmount = ((selectedItemsSum * serviceFee) + selectedItemsSum);
                    return { originalAmount: memoizedHungarianRound(calculatedAmount) };
                } else {
                    // Keep original amount unchanged, only update amount in the other useEffect
                    return { originalAmount: originalAmount };
                }
            } else {
                const roundedAmount = memoizedHungarianRound(cartTotalWithServiceFee);
                return { originalAmount: roundedAmount };
            }
        } else {
            if (hasSelectedItems) {
                const serviceFee = Number(serviceFeePercentage);
                return { originalAmount: Math.round((selectedItemsSum * serviceFee) + selectedItemsSum) };
            } else {
                return { originalAmount: cartTotalWithServiceFee };
            }
        }
    }, [currentTable, paymentType, selectedItemsToPay, selectedItemsSum, serviceFeePercentage, cartTotalWithServiceFee, memoizedHungarianRound, originalAmount]);

    // Apply the calculated values when payment type changes
    useEffect(() => {
        if (currentTable && paymentTypeCalculations.originalAmount !== 0) {
            setOriginalAmount(paymentTypeCalculations.originalAmount);
        }
    }, [currentTable, paymentTypeCalculations]);
    const handleSwitchChange = (value) => {
        setNeedInvoice(value);
        if (value) {
            setModalVisible(true);
        }
    };
    const handleSwitchChangeNav = (value) => {
        setNeedNAV(value);
    };

    const handleModalDismiss = () => {
        setModalVisible(false);
        setNeedInvoice(false);
        setVatInvoiceData(false);
    };
    useEffect(() => {
        if (vatInvoiceData) {
            setModalVisible(false)
        }
    }, [vatInvoiceData]);

    // Memoize input handlers to prevent unnecessary re-renders
    const handleInput = useCallback((value) => {
        setAmount((prev) => ((prev === originalAmount || prev === 0) ? value : prev + value));
    }, [originalAmount]);

    const handleBackspace = useCallback(() => {
        setAmount((prev) => (prev.length > 1 ? prev.slice(0, -1) : ''));
    }, []);

    const handleClear = useCallback(() => {
        setAmount(0);
    }, []);
    // Memoize canBeClosed function to prevent unnecessary recalculations
    const canBeClosed = useMemo(() => {
        if (isSplitOrder) {
            return ((paymentsSum >= (originalAmount - 4)));
        }
        return amountToPay <= 1;
    }, [isSplitOrder, paymentsSum, originalAmount, amountToPay]);

    // Memoize utility functions to prevent unnecessary re-renders
    const convertPaymentType = useCallback(() => {
        if (paymentType === "Készpénz") return "cash";
        if (paymentType === "Bankkártya") return "creditCard";
        if (paymentType === "Utalvány") return "voucher";
        if (paymentType === "VIP Számla") return "vipBalance";
        if (paymentType === "Személyzeti") return "employeeBalance";
        return "temporary";
    }, [paymentType]);

    const handleDeletePayment = useCallback((index) => {
        haptics.warning();
        setPayments(prevPayments => {
            const updatedPayments = [...prevPayments];
            updatedPayments.splice(index, 1);
            return updatedPayments;
        });
    }, []);


    // Memoize addPayment function to prevent unnecessary re-renders
    const addPayment = useCallback(() => {
        if (!paymentType) {
            if (Platform.OS === "android") {
                return ToastAndroid.showWithGravity(t('payment_select_method'), 5000, ToastAndroid.CENTER);
            } else {
                setProcessing(false);
                return alert(t('payment_select_method'));
            }
        }

        const newPayment = {
            "paymentType": convertPaymentType(),
            "change": 1,
            "currency": "HUF",
        };

        if (paymentType === 'Készpénz' || paymentType === 'cash') {
            newPayment.amount = memoizedHungarianRound(Number(amount)) || 0;
        } else {
            newPayment.amount = Number(amount) || 0;
        }

        setPayments(prevPayments => [...prevPayments, newPayment]);
        setAmount(amountToPay);
        setPaymentType(false);
    }, [paymentType, amount, amountToPay, convertPaymentType, memoizedHungarianRound]);

    const confirmRemovePayments = (onConfirm) => {
        if (Platform.OS === 'web') {
            if (window.confirm(t('existing_payments_remove_confirm'))) {
                onConfirm();
            }
        } else {
            Alert.alert(
                t('existing_payments_title'),
                t('existing_payments_remove_confirm'),
                [
                    {
                        text: t('cancel'),
                        style: "cancel"
                    },
                    {
                        text: t('yes'),
                        onPress: onConfirm
                    }
                ],
                { cancelable: false }
            );
        }

    };
    function handleSplitorderToggle() {
        if (isSplitOrder && payments.length > 0) {
            confirmRemovePayments(() => {
                setPayments([]);
                setIsSplitOrder(false);
            });
        } else {
            setIsSplitOrder(!isSplitOrder);
            if (!isSplitOrder) {
                setAmount(originalAmount - paymentsSum);
            }
        }
    }

    // Memoize payment type change handler to prevent unnecessary re-renders
    const handlePaymentTypeChange = useCallback((type) => {
        setPaymentType(type);
        if (type === 'Utalvány' || type === 'voucher') {
            setPaymentType('Utalvány'); // voucher
            setVoucherModalVisible(true);
        }

        // Log payment type change
        ErrorReportingService.info("Payment type changed", {
            type,
            isSplitOrder,
            hasAmount: amount > 0
        }, "PaymentScreen");

        // Second click on a payment type adds it to payments IF split order
        if (paymentType === type && isSplitOrder && amount > 0) {
            if (paymentType !== "voucher" && paymentType !== "Utalvány") {
                addPayment();
            }
        } else {
            // Calculate new amount based on payment type
            let newAmount = amount;

            if (type === 'Készpénz') {
                if (isSplitOrder) {
                    if (paymentsSum < originalAmount && (amount === 0 || !amount)) {
                        newAmount = memoizedHungarianRound(originalAmount - paymentsSum);
                    } else {
                        newAmount = memoizedHungarianRound(amount);
                    }
                } else {
                    newAmount = memoizedHungarianRound(amount);
                }
            } else if (type === 'Bankkártya') {
                if (cartTotalWithServiceFee && currentTable) {
                    if (!isSplitOrder) {
                        newAmount = cartTotalWithServiceFee;
                    } else {
                        if (paymentsSum < originalAmount && (amount === 0 || !amount)) {
                            if (selectedItemsToPay?.length > 0) {
                                newAmount = Math.round((selectedItemsSum * serviceFeePercentage) + selectedItemsSum - paymentsSum);
                            } else {
                                newAmount = cartTotalWithServiceFee - paymentsSum;
                            }
                        } else {
                            if (selectedItemsToPay?.length > 0) {
                                newAmount = Math.round((selectedItemsSum * serviceFeePercentage) + selectedItemsSum - paymentsSum);
                            } else {
                                newAmount = cartTotalWithServiceFee - paymentsSum;
                            }
                        }
                    }
                } else {
                    if (!isSplitOrder) {
                        newAmount = originalAmount;
                    } else {
                        if (paymentsSum < originalAmount) {
                            newAmount = originalAmount - paymentsSum;
                        }
                    }
                }
            } else if (type === 'VIP Számla' || type === 'Személyzeti') {
                newAmount = originalAmount - paymentsSum;
            } else {
                newAmount = originalAmount - paymentsSum;

                if (!isSplitOrder) {
                    newAmount = originalAmount - paymentsSum;
                } else {
                    if (paymentsSum < originalAmount && (amount === 0 || !amount)) {
                        newAmount = originalAmount - paymentsSum;
                    }
                }
            }

            // Update amount state once with the calculated value
            setAmount(newAmount);
        }
    }, [
        paymentType, isSplitOrder, amount, paymentsSum, originalAmount,
        cartTotalWithServiceFee, currentTable, selectedItemsToPay,
        selectedItemsSum, serviceFeePercentage, memoizedHungarianRound, addPayment
    ]);

    useEffect(() => {
        return () => {
            setProcessing(false);
        }
    }, []);

    useEffect(() => {
        const callback = async (response) => {
            try {
                if (response.responseData && response.responseData.status === "success" || response.responseData.status === "SUCCESS") {
                    setCart([]);
                    const tableOrder = orders?.find(
                        (order) =>
                            !order.storno &&
                            order.status !== "cancelled" &&
                            order.status !== "closed" &&
                            order.tableId === currentTable?._id
                    );
                    ErrorReportingService.info("Processing payment after successful response", {
                        paymentType,
                        tableOrderId: tableOrder?._id
                    }, "PaymentScreen");

                    await handlePayment(paymentType, paidAmount, originalAmount, setProcessing, navigation, currentTable && serviceFeeAmount > 0 ? cartTotalWithServiceFee : cartTotal, setCart, cartItems, sendData, () => {}, originalCartTotal, productsPrice, discountsForWholeCart, vatInvoiceData, needInvoice, true, currentTable, serviceFeeAmount, alreadySentCartItems, tableOrder?._id, payments, undefined, undefined, undefined, tableOrder, needNAVRef.current)
                    setIsModalVisible(false)
                    return setProcessing(false);
                } else {
                    setIsModalVisible(false)
                    setProcessing(false);
                    return navigation.navigate('FailedPayment', { ...response.responseData, orderResponse: null });
                }
            } catch (e) {
                setIsModalVisible(false)
                setProcessing(false);
                ErrorReportingService.error("Failed to redirect", { error: e }, "PaymentScreen");
            }
        }

        const handleUrl = async ({ url }) => {
            const response = VivaPaymentModule.handleCallbackUrl(url);
            return callback(response)
        };

        const subscription = Linking.addEventListener('url', handleUrl);

        return () => {
            subscription.remove();
        };
    }, []);
    const printReceiptAndTicket = async (order) => {
        if (Platform.OS !== "web") {
            try {
                const hasConfiguredPrinter = await hasAnyConfiguredPrinter();
                if (!hasConfiguredPrinter) {
                    return true;
                }

                // Print receipt tickets
                await RawBTPrintTicket(order, { showErrorDialog: false });

                if (cartItems.length > 0) {
                    await new Promise((resolve) => globalThis.setTimeout(resolve, 500));
                    await RawBTPrintKitchen(order, order.items, { showErrorDialog: false });
                }

            } catch (e) {
                if (e?.skipped === true) {
                    return true;
                }

                if (Platform.OS === "android") {
                    return ToastAndroid.showWithGravity(t('payment_print_failed'), ToastAndroid.LONG, ToastAndroid.BOTTOM, 50);
                } else {
                    return alert(t('payment_print_failed'));
                }
            }
        }
    }
    async function handleProforma() {
        try {
            setProcessing(true);
            const proforma = true;
            const tableOrder = orders?.find(
                (order) =>
                    !order.storno &&
                    order.status !== "cancelled" &&
                    order.status !== "closed" &&
                    order.tableId === currentTable?._id
            );
            const storeOrderResponse = await send_storeOrder(cartTotal, cartItems, "", 0, discountsForWholeCart, undefined, false,
                currentTable, serviceFeeAmount, true, alreadySentCartItems, tableOrder?._id, [],
                0, currentTable && serviceFeeAmount > 0 ? cartTotalWithServiceFee : cartTotal, (selectedVipCard ? selectedVipCard : selectedEmployee), undefined, selectedItemsToPay, tableOrder, proforma);
            printReceiptAndTicket(storeOrderResponse);
            upsertOrder?.(storeOrderResponse);

            if (setIsModalVisible !== undefined) {
                setIsModalVisible(false);
            }

            navigation.navigate('TablesScreen');
            removeEmployee();
            removeVipCard();

        } catch (error) {
            ErrorReportingService.error("Error handling proforma", { error }, "PaymentScreen");
            if (Platform.OS === 'android') {
                ToastAndroid.showWithGravity(
                    t('payment_proforma_error'),
                    ToastAndroid.LONG,
                    ToastAndroid.CENTER
                );
            } else {
                alert(t('payment_proforma_error'));
            }
        } finally {
            setProcessing(false);
        }
    }

    const renderSplitItemsPanel = () => {
        if (!(currentTable && showSplitItems)) {
            return null;
        }

        return (
            <View style={[styles.splitPanel, isCompactDevice && styles.splitPanelCompact]}>
                <View style={styles.splitPanelHeader}>
                    <Text style={styles.splitPanelTitle}>{t("split_items_to_pay")}</Text>
                </View>
                <FlatList
                    data={combinedCartItems}
                    keyExtractor={(item, index) => `${item.id || item._id || 'item'}-${index}`}
                    renderItem={renderItem}
                    contentContainerStyle={styles.splitPanelListContent}
                    style={[styles.splitPanelList, !isCompactDevice && {height: screenHeight * 0.86}]}
                    scrollEnabled
                    initialNumToRender={10}
                    maxToRenderPerBatch={5}
                    windowSize={5}
                    removeClippedSubviews={Platform.OS === 'android'}
                />
            </View>
        );
    };

    return (
        <View style={[styles.container, { width: '100%', height: "100%" }]}>
            <LinearGradient
                colors={isDark ? [theme.colors.background, theme.colors.card, theme.colors.background] : ['#EAE5EE', '#FEE6DE', '#EBE5F2']}
                style={[styles.gradientBackground, !isCompactDevice && styles.gradientBackgroundWide]}
            >
                <Spinner
                    visible={processing}
                    textContent={t("processing...")}
                    textStyle={styles.spinnerTextStyle}
                />
                <View style={[styles.primaryPanel, isCompactDevice && styles.primaryPanelCompact]}>
                    <ScrollView
                        style={styles.paymentScroll}
                        contentContainerStyle={[
                            styles.paymentScrollContent,
                            isCompactDevice && styles.paymentScrollContentCompact,
                            useDenseLayout && styles.paymentScrollContentDense,
                            {paddingBottom: compactFooterSpacer},
                        ]}
                        showsVerticalScrollIndicator={false}
                        keyboardShouldPersistTaps="handled"
                        keyboardDismissMode="on-drag"
                    >
                    <View style={[styles.tabsContainer, isCompactDevice && styles.tabsContainerCompact, useDenseLayout && styles.tabsContainerDense]}>
                        {paymentOptions.map(({ type, icon }) => (
                            <TouchableOpacity
                                key={type}
                                style={[styles.tab, isCompactDevice && styles.tabCompact, useDenseLayout && styles.tabDense, paymentType === type && styles.activeTab]}
                                onPress={() => handlePaymentTypeChange(type)}
                            >
                                <Feather name={icon} size={isCompactDevice ? 20 : (useDenseLayout ? 16 : 18)} color={paymentType === type ? theme.colors.primary : theme.colors.subText} />
                                <Text style={[styles.tabText, isCompactDevice && styles.tabTextCompact, useDenseLayout && styles.tabTextDense, paymentType === type && styles.activeTabText]}>{translateDisplay(type)}</Text>
                            </TouchableOpacity>
                        ))}
                        {currentTable && (<TouchableOpacity
                            style={[styles.tab, isCompactDevice && styles.tabCompact, useDenseLayout && styles.tabDense, showSplitItems && styles.activeTab]}
                            onPress={() => { setShowSplitItems(!showSplitItems) }}
                        >
                            <Feather name="users" size={isCompactDevice ? 20 : (useDenseLayout ? 16 : 18)} color={showSplitItems ? theme.colors.primary : theme.colors.subText} />
                            <Text style={[styles.tabText, isCompactDevice && styles.tabTextCompact, useDenseLayout && styles.tabTextDense, showSplitItems && styles.activeTabText]}>{t('split_payment')}</Text>
                        </TouchableOpacity>)}
                        {currentTable && (<TouchableOpacity
                            style={[styles.tab, isCompactDevice && styles.tabCompact, useDenseLayout && styles.tabDense, processing && { opacity: 0.7 }]}
                            onPress={() => {
                                handleProforma()
                            }}
                            disabled={processing}
                        >
                            <Feather name="file-text" size={isCompactDevice ? 20 : (useDenseLayout ? 16 : 18)} color={processing ? theme.colors.placeholder : theme.colors.subText} />
                            <Text style={[styles.tabText, isCompactDevice && styles.tabTextCompact, useDenseLayout && styles.tabTextDense, processing && { color: theme.colors.placeholder }]}>{t("proforma")}</Text>
                        </TouchableOpacity>)}
                        {/*<TouchableOpacity*/}
                        {/*    style={[styles.tab, isSplitOrder && styles.splitTab]}*/}
                        {/*    onPress={() => {*/}
                        {/*todo refactor it for bontott for now every order is "split" order so every order can be paid with multiple payment options*/}
                        {/*        handleSplitorderToggle()*/}
                        {/*    }}*/}
                        {/*>*/}
                        {/*    <View style={{ flexDirection: 'row' }}>*/}
                        {/*        <View style={{ flexDirection: 'column', paddingRight: "4px" }}>*/}
                        {/*            <Feather name={"dollar-sign"} size={10} color={isSplitOrder ? 'white' : '#666'} />*/}
                        {/*            <Feather name={"credit-card"} size={10} color={isSplitOrder ? 'white' : '#666'} />*/}
                        {/*        </View>*/}
                        {/*        <Feather name={"shuffle"} size={19} color={isSplitOrder ? 'white' : '#666'} />*/}
                        {/*    </View>*/}
                        {/*    <Text style={[styles.tabText, isSplitOrder && styles.activeSplitTabText]}>Bontott</Text>*/}
                        {/*</TouchableOpacity>*/}
                    </View>
                    {selectedVipCard && <View style={styles.vipCardContainer}>
                        {(selectedVipCard?.user?.name?.first.length < 1 && selectedVipCard?.user?.name?.last.length < 1)
                            || (!selectedVipCard?.user?.name && !selectedVipCard?.user?.name)
                            ?
                            <Text style={styles.vipCardText}>
                                {selectedVipCard.cardId}
                            </Text> : <Text style={styles.vipCardText}>
                                {selectedVipCard?.user?.name?.first} {selectedVipCard?.user?.name?.last}
                            </Text>}
                    </View>}
                    {selectedEmployee && <View style={styles.vipCardContainer}>
                        {(selectedEmployee?.name?.first.length < 1 && selectedEmployee?.name?.last.length < 1)
                            || (!selectedEmployee?.name && !selectedEmployee?.name)
                            ?
                            <Text style={styles.vipCardText}>
                                {t("unnamed")}
                            </Text> : <Text style={styles.vipCardText}>
                                {selectedEmployee?.name?.first} {selectedEmployee?.name?.last}
                            </Text>}
                    </View>}
                    {/* Payment Amount Input */}
                    <View style={[styles.amountContainer, isCompactDevice && styles.amountContainerCompact, useDenseLayout && styles.amountContainerDense]}>
                        {/*{isSplitOrder && <TouchableOpacity onPress={addPayment} >*/}
                        {/*    <Feather name={"plus-square"} size={28} color="black"/>*/}
                        {/*</TouchableOpacity>}*/}

                        <TouchableOpacity onPress={handleClear} style={styles.clearButton}>
                            <Feather name="trash-2" size={28} color={theme.colors.text} />
                        </TouchableOpacity>
                        <TextInput
                            style={[styles.amountInput, isCompactDevice && styles.amountInputCompact, useDenseLayout && styles.amountInputDense]}
                            value={formatAmountWithSpaces(amount)}
                            editable={Platform.OS !== 'android'}
                            keyboardType="numeric"
                            placeholder={t("received_amount")}
                            placeholderTextColor={theme.colors.placeholder}
                            onFocus={() => {
                                if (Platform.OS !== 'android') {
                                    setAmount('');
                                }
                            }}
                            onChangeText={(text) => {
                                // Allow only numbers and one decimal point
                                const numericText = text.replace(/[^0-9.]/g, '');
                                setAmount(numericText);
                            }}
                            ref={textInputRef}
                            showSoftInputOnFocus={Platform.OS !== 'android'}
                            caretHidden={Platform.OS === 'android'}
                        />
                        <TouchableOpacity onPress={handleBackspace} style={styles.backspaceButton}>
                            <Text style={styles.backspaceText}>⌫</Text>
                        </TouchableOpacity>
                    </View>
                    <View style={[styles.paymentBodyLayout, isCompactDevice && styles.paymentBodyLayoutCompact, useDenseLayout && styles.paymentBodyLayoutDense]}>
                        <View style={[styles.summaryColumn, isCompactDevice && styles.summaryColumnCompact, useDenseLayout && styles.summaryColumnDense]}>
                            {/* Info Lines */}
                            {(cartItems.length > 0 || alreadySentCartItems?.length > 0) &&
                                <View style={[styles.infoContainer, isCompactDevice && styles.infoContainerCompact, useDenseLayout && styles.infoContainerDense]}>
                                    <View style={styles.infoRow}>
                                        <Text style={styles.infoLabel}>{t("payable")}:</Text>
                                        <Text style={styles.infoValue}>{(originalAmount)}</Text>
                                    </View>
                                    <View style={[styles.infoRow, {
                                        color: Number(amountToPay) > 0 ? theme.colors.error : theme.colors.text,
                                        fontWeight: Number(amountToPay) > 0 ? "900" : "200"
                                    }]}>
                                        {(amountToPay >= 1 && !isSplitOrder) && (<><Text style={[styles.infoLabel, {
                                            color: Number(amountToPay) > 0 ? theme.colors.error : theme.colors.text,
                                            fontWeight: Number(amountToPay) > 0 ? "900" : "200"
                                        }]}>{t("amount_due")}:</Text>
                                            <Text style={[styles.infoValue, {
                                                color: Number(amountToPay) > 0 ? theme.colors.error : theme.colors.text,
                                                fontWeight: Number(amountToPay) > 0 ? "900" : "200"
                                            }]}>{priceFormatter(amountToPay)}</Text></>)}
                                        {(isSplitOrder) && (<><TouchableOpacity disabled
                                            onPress={() => toggleModal()}><Text style={[styles.infoLabel, {
                                                color: Math.round(paymentsSum) >= Math.round(originalAmount) ? theme.colors.success : theme.colors.primary,
                                                fontWeight: "900",
                                            }]}>{t("paid_so_far")}:</Text></TouchableOpacity>

                                            <TouchableOpacity disabled onPress={() => toggleModal()}><Text
                                                style={[styles.infoValue, {
                                                    color: Math.round(paymentsSum) >= Math.round(originalAmount) ? theme.colors.success : theme.colors.primary,
                                                    fontWeight: "900",
                                                }]}>{priceFormatter(paymentsSum)}</Text></TouchableOpacity></>)}
                                    </View>
                                    {Number(changeToReturn) > 1 && <View style={styles.infoRow}>
                                        {(paymentType === 'Bankkártya' && !shouldTreatSurplusAsChange) ?
                                            Number(changeToReturn) > 1 && (<>
                                                <Text
                                                    style={[styles.infoLabel, { fontWeight: Number(changeToReturn) > 1 ? "900" : "200" }]}>
                                                    {t("overpayment")}:
                                                </Text>
                                                <Text
                                                    style={[styles.infoValue, { fontWeight: Number(changeToReturn) > 1 ? "900" : "200" }]}>
                                                    {priceFormatter(changeToReturn)}
                                                </Text>
                                            </>)
                                            :
                                            changeToReturn > 4 && <>
                                                <Text
                                                    style={[styles.infoLabel, { fontWeight: Number(changeToReturn) > 4 ? "900" : "200" }]}>
                                                    {t("change")}:
                                                </Text>
                                                <Text
                                                    style={[styles.infoValue, { fontWeight: Number(changeToReturn) > 4 ? "900" : "200" }]}>
                                                    {priceFormatter(hungarianRoundNumbers(changeToReturn))}
                                                </Text>
                                            </>
                                        }


                                    </View>}

                                </View>}
                            {payments?.length > 0 && (
                                <View style={[styles.paidListCard, isCompactDevice && styles.paidListCardCompact]}> 
                                    <FlatList
                                        data={payments}
                                        keyExtractor={(item, index) => index.toString()}
                                        style={styles.paymentsList}
                                        contentContainerStyle={styles.paymentsListContent}
                                        initialNumToRender={5}
                                        maxToRenderPerBatch={5}
                                        windowSize={3}
                                        removeClippedSubviews={Platform.OS === 'android'}
                                        renderItem={({ item, index }) => (
                                            <View style={styles.paymentItem}>
                                                <Text
                                                    style={styles.paymentType}>{reverseConvertPaymentType(item.paymentType)}</Text>
                                                <Text style={styles.paymentAmount}>{priceFormatter(item.amount)}</Text>
                                                <TouchableOpacity onPress={() => handleDeletePayment(index)}>
                                                    <Feather name="trash-2" size={22} color={theme.colors.error} />
                                                </TouchableOpacity>
                                            </View>
                                        )}
                                    />
                                </View>)}
                        </View>
                        <View style={[styles.controlsColumn, isCompactDevice && styles.controlsColumnCompact, useDenseLayout && styles.controlsColumnDense]}>{/*2*/}


                            <View style={[styles.switchContainer, isCompactDevice && styles.switchContainerCompact, useDenseLayout && styles.switchContainerDense]}>
                                <Text style={styles.switchLabel}>{t("request_invoice")}</Text>
                                <Switch
                                    vibrate={true}
                                    thumbColor={theme.colors.primary}
                                    trackColor={{ false: "#767577", true: "#ecbb69" }}
                                    ios_backgroundColor="#3e3e3e"
                                    value={needInvoice}
                                    onValueChange={handleSwitchChange}
                                />
                                <VatModal visible={modalVisible} onSubmit={setVatInvoiceData} onDismiss={handleModalDismiss} />
                            </View>
                            <View style={[styles.switchContainer, isCompactDevice && styles.switchContainerCompact, useDenseLayout && styles.switchContainerDense]}>
                                <Text style={styles.switchLabel}>{t('payment_cash_register_print')}</Text>
                                <Switch
                                    vibrate={true}
                                    thumbColor={theme.colors.primary}
                                    trackColor={{ false: "#767577", true: "#ecbb69" }}
                                    ios_backgroundColor="#3e3e3e"
                                    value={needNAV}
                                    onValueChange={handleSwitchChangeNav}
                                />
                                <VatModal visible={modalVisible} onSubmit={setVatInvoiceData} onDismiss={handleModalDismiss} />
                            </View>
                            {(!isCompactDevice || !isKeyboardVisible) && (
                                <View style={[styles.pinpadContainer, {height: pinPadHeight}, isCompactDevice && styles.pinpadContainerCompact]}>
                                    <PinPad onButtonClick={(value) => (value === 'X' ? addPayment() : handleInput(value.toString()))} />
                                </View>
                            )}
                        </View>
                    </View>
                    {isCompactDevice && renderSplitItemsPanel()}
                    </ScrollView>
                    {/* Pinpad and Process Payment Button Container */}
                    {(cartItems.length > 0 || alreadySentCartItems?.length > 0) && <View style={[
                        styles.bottomContainer,
                        isCompactDevice && styles.bottomContainerCompact,
                        styles.bottomContainerSticky,
                        {paddingBottom: bottomActionInset},
                        useStickyFooter && { bottom: Math.max(0, keyboardHeight - insets.bottom) }
                    ]}> 
                        {/* Pinpad */}


                        {/* Back and Process Payment Buttons */}
                        {(cartItems.length > 0 || alreadySentCartItems?.length > 0) && <View style={[styles.buttonContainer, isCompactDevice && styles.buttonContainerCompact]}>
                            <TouchableOpacity style={[styles.backButton, isCompactDevice && styles.footerSecondaryButtonCompact]} onPress={() => {
                                if (setIsModalVisible !== undefined) {
                                    setIsModalVisible(false)
                                }

                                navigation.navigate('HomeScreen');
                            }}>
                                <Feather name="arrow-left" size={24} color="#fff" />
                            </TouchableOpacity>

                            {Math.round(paymentsSum) > Math.round(originalAmount) + 2 ? (
                                <>
                                    {showTipButton && (
                                        <TouchableOpacity
                                            style={[styles.borravaloButton, isCompactDevice && styles.footerSecondaryButtonCompact]}
                                            onPress={() => {
                                                confirmPaymentAmount(true)
                                            }}
                                            disabled={processing}
                                        >
                                            <Text style={styles.processButtonText}>{t("tip")}</Text>
                                        </TouchableOpacity>
                                    )}
                                    <TouchableOpacity
                                        style={[
                                            showTipButton ? styles.noHorizontalPaddingButton : styles.processButton,
                                            isCompactDevice && styles.processButtonCompact,
                                            processing && { opacity: 0.7 }
                                        ]}
                                        onPress={() => { confirmPaymentAmount(false) }}
                                        disabled={processing}
                                    >
                                        <Text style={styles.processButtonText}>
                                            {t("change")}: {priceFormatter(hungarianRoundNumbers(isSplitOrder ? paymentsSum - originalAmount : changeToReturn))}
                                        </Text>
                                    </TouchableOpacity>
                                </>
                            ) : (
                                <TouchableOpacity
                                    style={[
                                        styles.processButton,
                                        isCompactDevice && styles.processButtonCompact,
                                        {
                                            backgroundColor: ((!isSplitOrder && amountToPay > 1) || (isSplitOrder && paymentsSum < originalAmount - 1)) ? "#f3a965" : "#ff8000",
                                            opacity: processing ? 0.7 : 1
                                        }
                                    ]}
                                    disabled={processing || ((!isSplitOrder && amountToPay > 1) || (isSplitOrder && paymentsSum < originalAmount - 4))}
                                    onPress={confirmPaymentAmount}
                                >
                                    {((!isSplitOrder && amountToPay > 1) || (isSplitOrder && paymentsSum < originalAmount - 4)) &&
                                        <Text style={styles.processButtonText}>
                                            {t("amount_due")}: {isSplitOrder ? priceFormatter(originalAmount - paymentsSum) : priceFormatter(amountToPay)}
                                        </Text>
                                    }
                                    {canBeClosed &&
                                        <Text style={styles.processButtonText}>
                                            {processing ? t("processing...") : t("store_recept")}
                                        </Text>
                                    }
                                </TouchableOpacity>
                            )}
                        </View>}
                    </View>}</View>
                {!isCompactDevice && renderSplitItemsPanel()}

                {(isSplitOrder && paymentsModal) && (
                    <Modal
                        visible={paymentsModal}
                        transparent={true}
                        animationType="fade"
                        onRequestClose={toggleModal}
                    >
                        <View style={styles.modalBackdrop}>
                            <View style={styles.modalContainer}>
                                <View style={styles.modalHeader}>
                                    <Text style={styles.modalTitle}>{t("paid_so_far")}</Text>
                                    <TouchableOpacity onPress={toggleModal}>
                                        <Feather name="x" size={24} color={theme.colors.text} />
                                    </TouchableOpacity>
                                </View>

                                <FlatList
                                    data={payments}
                                    keyExtractor={(item, index) => index.toString()}
                                    style={{ maxHeight: screenHeight * 0.4 }}
                                    contentContainerStyle={{ flexGrow: 1 }}
                                    initialNumToRender={5}
                                    maxToRenderPerBatch={5}
                                    windowSize={3}
                                    removeClippedSubviews={Platform.OS === 'android'}
                                    renderItem={({ item, index }) => (
                                        <View style={styles.paymentItem}>
                                            <Text style={styles.paymentType}>{reverseConvertPaymentType(item.paymentType)}</Text>
                                            <Text style={styles.paymentAmount}>{priceFormatter(item.amount)}</Text>
                                            <TouchableOpacity onPress={() => handleDeletePayment(index)}>
                                                <Feather name="trash-2" size={22} color={theme.colors.error} />
                                            </TouchableOpacity>
                                        </View>
                                    )}
                                />

                                <TouchableOpacity style={styles.closeButton} onPress={toggleModal}>
                                    <Text style={styles.closeButtonText}>{t("close")}</Text>
                                </TouchableOpacity>
                            </View>
                        </View>
                    </Modal>
                )}
            </LinearGradient>
            <VoucherModal
                visible={voucherModalVisible}
                onClose={() => setVoucherModalVisible(false)}
                onConfirm={validateAndAddVoucher}
                voucherCode={voucherCode}
                onVoucherCodeChange={setVoucherCode}
                styles={styles}
            />
            <HappyAlert
                visible={isAlertVisible}
                onConfirm={() => setIsAlertVisible(false)}
                onCancel={() => setIsAlertVisible(false)}
                type="error"
                message={t("voucher_code_is_invalid")}
            />

            {/* Add Teya Payment Dialog */}
            <TeyaPaymentDialog />
            <NpipePaymentDialog />

        </View>
    );
};

const buildStyles = (theme) => ({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background,
        zIndex: 1000,
    },
    gradientBackground: {
        flex: 1,
        width: '100%',
        height: '100%',
        flexDirection: 'column',
    },
    gradientBackgroundWide: {
        flexDirection: 'row',
    },
    primaryPanel: {
        flex: 2,
        minWidth: 0,
        position: 'relative',
    },
    primaryPanelCompact: {
        flex: 1,
        position: 'relative',
    },
    paymentScroll: {
        flex: 1,
    },
    paymentScrollContent: {
        flexGrow: 1,
    },
    paymentScrollContentCompact: {
        paddingBottom: 8,
        minHeight: '100%',
    },
    paymentScrollContentDense: {
        minHeight: '100%',
    },
    modalContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(0,0,0,0.5)'
    },
    modalContent: {
        backgroundColor: theme.colors.card,
        padding: 20,
        borderRadius: 10,
        width: '80%',
        maxWidth: 400
    },
    input: {
        borderWidth: 1,
        borderColor: theme.colors.border,
        borderRadius: 5,
        padding: 10,
        marginBottom: 15
    },
    modalButtons: {
        flexDirection: 'row',
        justifyContent: 'flex-end'
    },
    modalButton: {
        marginLeft: 10,
        padding: 10
    },
    splitTab: {
        backgroundColor: theme.colors.primary,
    },
    activeSplitTabText: {
        color: 'white',
    },
    itemContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingHorizontal: 10,
        paddingVertical: 16,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.border,
        backgroundColor: theme.colors.card
    },
    itemName: {
        fontSize: 16,
        fontWeight: 'bold'
    },
    unitPrice: {
        fontSize: 14,
        color: theme.colors.subText
    },
    extraText: {
        fontSize: 12,
        color: theme.colors.subText
    },
    itemPrice: {
        fontSize: 16,
        fontWeight: 'bold'
    },
    tabsContainer: {
        flexDirection: 'row',
        justifyContent: 'space-around',
        paddingHorizontal: 8,
        paddingVertical: 8,
        backgroundColor: theme.colors.card,
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: theme.colors.border,
    },
    tabsContainerCompact: {
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        paddingHorizontal: 6,
        paddingTop: 8,
        paddingBottom: 6,
    },
    tabsContainerDense: {
        paddingHorizontal: 6,
        paddingVertical: 6,
    },
    tab: {
        flex: 1,
        padding: 12,
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 0,
    },
    tabCompact: {
        flexBasis: '25%',
        flexGrow: 0,
        paddingHorizontal: 6,
        paddingVertical: 10,
    },
    tabDense: {
        paddingHorizontal: 8,
        paddingVertical: 8,
    },
    activeTab: {
        borderBottomWidth: 2,
        borderBottomColor: theme.colors.primary,
    },
    tabText: {
        fontSize: 16,
        color: theme.colors.subText,
        marginTop: 4,
        textAlign: 'center',
    },
    tabTextCompact: {
        fontSize: 13,
        lineHeight: 18,
    },
    tabTextDense: {
        fontSize: 13,
        lineHeight: 16,
    },
    activeTabText: {
        color: theme.colors.primary,
    },
    paymentBodyLayout: {
        flexDirection: 'row',
        paddingHorizontal: 10,
    },
    paymentBodyLayoutCompact: {
        flexDirection: 'column',
        paddingHorizontal: 12,
    },
    paymentBodyLayoutDense: {
        paddingHorizontal: 8,
    },
    summaryColumn: {
        flex: 2,
        paddingLeft: 10,
        minWidth: 0,
    },
    summaryColumnCompact: {
        flex: 0,
        paddingLeft: 0,
    },
    summaryColumnDense: {
        flex: 1.65,
        paddingLeft: 6,
    },
    controlsColumn: {
        flex: 3,
        paddingLeft: 10,
        minWidth: 0,
    },
    controlsColumnCompact: {
        flex: 0,
        paddingLeft: 0,
        marginTop: 10,
    },
    controlsColumnDense: {
        flex: 2.35,
        paddingLeft: 8,
    },
    infoContainer: {
        marginTop: 16,
        padding: 14,
        backgroundColor: theme.colors.card,
        borderRadius: 18,
        borderWidth: 1,
        borderColor: theme.colors.border,
        shadowColor: '#0f172a',
        shadowOffset: {width: 0, height: 6},
        shadowOpacity: 0.05,
        shadowRadius: 14,
        elevation: 1,
    },
    infoContainerCompact: {
        marginTop: 12,
        padding: 12,
    },
    infoContainerDense: {
        marginTop: 10,
        padding: 10,
    },
    infoRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginVertical: 4,
        gap: 10,
    },
    infoLabel: {
        fontSize: 17,
        color: theme.colors.text,
        fontWeight: "500",
        flex: 1,
    },
    infoValue: {
        fontSize: 17,
        color: theme.colors.text,
        fontWeight: 'bold',
        textAlign: 'right',
    },
    amountContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        marginHorizontal: 12,
        marginTop: 12,
        paddingHorizontal: 10,
        paddingVertical: 12,
        alignSelf: 'stretch',
        backgroundColor: theme.colors.card,
        borderRadius: 24,
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    amountContainerCompact: {
        marginHorizontal: 12,
        marginTop: 10,
        paddingHorizontal: 8,
        paddingVertical: 10,
    },
    amountContainerDense: {
        marginHorizontal: 10,
        marginTop: 10,
        paddingHorizontal: 8,
        paddingVertical: 8,
    },
    backspaceButton: {
        marginRight: 10,
        marginLeft: 5,
    },
    backspaceText: {
        fontSize: 26,
        color: theme.colors.text,
    },
    amountInput: {
        marginTop: 4,
        fontSize: 30,
        textAlign: 'center',
        color: theme.colors.text,
        borderBottomWidth: 1,
        width: "75%",
        paddingVertical: 4,
    },
    amountInputCompact: {
        fontSize: 26,
        width: '68%',
    },
    amountInputDense: {
        fontSize: 24,
        width: '70%',
        paddingVertical: 2,
    },
    clearButton: {
        marginLeft: 10,
        marginRight: 10,
    },
    clearText: {
        fontSize: 26,
        color: theme.colors.text,
    },
    bottomContainer: {
        borderTopWidth: StyleSheet.hairlineWidth,
        borderTopColor: theme.colors.border,
        backgroundColor: theme.colors.card,
        paddingTop: 10,
        paddingHorizontal: 12,
        shadowColor: '#0f172a',
        shadowOffset: {width: 0, height: -8},
        shadowOpacity: 0.08,
        shadowRadius: 18,
        elevation: 10,
    },
    bottomContainerCompact: {
        paddingTop: 8,
    },
    bottomContainerSticky: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 40,
    },
    pinpadContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'center',
        width: "100%",
        minHeight: 280,
        paddingHorizontal: 0,
        paddingBottom: 10,
        marginTop: 6,
        marginBottom: 6,
        backgroundColor: theme.colors.card,
        borderRadius: 22,
        overflow: 'hidden',
    },
    pinpadContainerCompact: {
        minHeight: 260,
        marginBottom: 0,
    },
    noHorizontalPaddingButton: {
        backgroundColor: '#F76E24',
        alignItems: 'center',
        paddingTop: 16,
        paddingBottom: 16,
        flex: 1,
        marginLeft: 0,
        borderRadius: 16,
        paddingLeft: 3,
        paddingRight: 3,
    },
    borravaloButton: {
        backgroundColor: theme.colors.primary,
        alignItems: 'center',
        paddingTop: 16,
        paddingBottom: 16,
        paddingLeft: 10,
        paddingRight: 10,
        borderRadius: 16,
        width: 'auto',
    },
    buttonContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 0,
        gap: 10,
    },
    buttonContainerCompact: {
        flexDirection: 'column',
        alignItems: 'stretch',
    },
    backButton: {
        backgroundColor: 'black',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        borderRadius: 16,
        minWidth: 60,
    },
    footerSecondaryButtonCompact: {
        width: '100%',
        minWidth: 0,
    },
    processButton: {
        backgroundColor: '#F76E24',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 16,
        paddingHorizontal: 14,
        flex: 1,
        marginLeft: 0,
        borderRadius: 16,
        minHeight: 58,
    },
    processButtonCompact: {
        width: '100%',
    },
    processButtonText: {
        fontSize: 18,
        color: '#fff',
        fontWeight: 'bold',
        paddingHorizontal: 5,
        textAlign: 'center',
    },
    spinnerTextStyle: {
        color: '#FFF'
    },
    switchContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 16,
        paddingVertical: 14,
        marginTop: 10,
        backgroundColor: theme.colors.card,
        borderRadius: 18,
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    switchContainerCompact: {
        paddingHorizontal: 14,
        paddingVertical: 12,
    },
    switchContainerDense: {
        paddingHorizontal: 12,
        paddingVertical: 10,
        marginTop: 8,
    },
    switchLabel: {
        fontSize: 16,
        color: theme.colors.text,
        flex: 1,
        paddingRight: 12,
    },
    modalBackdrop: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    modalHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.border,
        paddingBottom: 10,
        marginBottom: 10,
    },
    modalTitle: {
        fontSize: 20,
        fontWeight: 'bold',
        color: theme.colors.text,
    },
    paymentItem: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: 12,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.border,
    },
    paymentType: {
        fontSize: 16,
        color: theme.colors.text,
        flex: 1,
    },
    paymentAmount: {
        fontSize: 16,
        fontWeight: 'bold',
        color: theme.colors.text,
        flex: 1,
        textAlign: 'right',
        marginRight: 15,
    },
    paidListCard: {
        marginTop: 12,
        paddingHorizontal: 12,
        paddingVertical: 8,
        backgroundColor: theme.colors.card,
        borderRadius: 18,
        borderWidth: 1,
        borderColor: theme.colors.border,
        maxHeight: 260,
    },
    paidListCardCompact: {
        maxHeight: 220,
    },
    paymentsList: {
        maxHeight: 240,
    },
    paymentsListContent: {
        paddingBottom: 4,
    },
    splitPanel: {
        flex: 1,
        backgroundColor: theme.colors.card,
        height: '100%',
        borderLeftWidth: StyleSheet.hairlineWidth,
        borderLeftColor: theme.colors.border,
    },
    splitPanelCompact: {
        flex: 0,
        marginTop: 14,
        borderLeftWidth: 0,
        borderRadius: 20,
        overflow: 'hidden',
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    splitPanelHeader: {
        paddingVertical: 18,
        paddingHorizontal: 12,
        backgroundColor: theme.colors.card,
        marginBottom: 6,
    },
    splitPanelTitle: {
        fontSize: 22,
        fontWeight: '800',
        color: theme.colors.text,
        textAlign: 'center',
    },
    splitPanelList: {
        flexGrow: 0,
    },
    splitPanelListContent: {
        paddingBottom: 20,
    },
    closeButton: {
        marginTop: 20,
        padding: 12,
        backgroundColor: theme.colors.primary,
        borderRadius: 8,
        alignItems: 'center',
    },
    closeButtonText: {
        fontSize: 18,
        fontWeight: 'bold',
        color: '#fff',
    },
    vipCardContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors.primary,
        padding: 10,
        borderRadius: 5,
    },
    vipCardText: {
        flex: 1,
        color: 'white',
        fontSize: 16,
        textAlign: 'center',
        fontWeight: 'bold',
    },
});

export default PaymentScreen;
